/**
 * Zod schemas mirroring the Firestore document shapes. Forms reuse
 * these so that validation rules live in one place. Pydantic mirrors
 * on the backend — drift is caught when functions reject docs that
 * the UI accepted.
 */

import { z } from 'zod';
import type { Timestamp } from 'firebase/firestore';

const timestamp = z.custom<Timestamp>(
  (v) => v != null && typeof (v as { toDate?: unknown }).toDate === 'function',
  'expected a Firestore Timestamp',
);

export const userProfileSchema = z.object({
  email: z.string().email(),
  displayName: z.string().max(100).optional(),
  locale: z.string().max(16).optional(),
  createdAt: timestamp.optional(),
  updatedAt: timestamp.optional(),
});
export type UserProfile = z.infer<typeof userProfileSchema>;

// "Personality" phrases the caregiver can override on the Voice page.
// All optional; absence => use the Python default in spoken_summary.py.
// Keys + max-200-char limit mirrored in firestore.rules / isValidPhrases.
export const phrasesSchema = z
  .object({
    greeting: z.string().max(200).optional(),         // {name}
    bedtimeEarly: z.string().max(200).optional(),
    bedtimeLate: z.string().max(200).optional(),
    nightVeryClose: z.string().max(200).optional(),
    nightClose: z.string().max(200).optional(),
    nightFar: z.string().max(200).optional(),         // {hours}
    noEventsMorning: z.string().max(200).optional(),
    noEventsAfternoon: z.string().max(200).optional(),
    // Played on loop by the device when a notification fires, until
    // the elder presses or the event ends. Supports {name},
    // {title}, {time} template variables. An empty string means
    // "silent notifications — LED only".
    notificationPrompt: z.string().max(200).optional(),   // {name} {title} {time}
    // Played on loop at wake time (like notificationPrompt) when the
    // "Morning notification" is enabled and the elder hasn't pressed since
    // waking, inviting a press to hear the daily summary. Only {name} — no
    // event vars, so one account-level clip. Empty = silent (LED only).
    morningPrompt: z.string().max(200).optional(),        // {name}
  })
  .strict();
export type Phrases = z.infer<typeof phrasesSchema>;

export const accountSchema = z.object({
  elderName: z.string().max(100),
  timeZone: z.string().max(64),
  // Awake-hours window. Used by the daily summary to pick day vs night
  // framing AND by the Activity timeline's day-band rendering, so it
  // belongs to the elder, not to any one voice profile. Mirrors
  // spoken_summary.py constants.
  wakeHour: z.number().int().min(4).max(11),
  bedtimeHour: z.number().int().min(18).max(23),
  // Written only by the Stripe subscription webhook. Absent ⇒ no subscription
  // (no longer defaulted to 'trial' at account creation, which mislabelled
  // every never-subscribed account). Entitlement is `serviceActivated`, not
  // this; this is a display mirror of real Stripe state.
  subscriptionStatus: z.enum(['trial', 'active', 'cancelled']).optional(),
  // One-way entitlement latch, set by the subscription webhook the first time
  // a subscription is live. The physical button (voice_button_press) speaks
  // its summary only once this is true, and a later lapse never clears it
  // (no auto-stop); the in-app preview is never gated. Absent until the first
  // subscription. See web/functions/billing.py + main.py.
  serviceActivated: z.boolean().optional(),
  // Server-written on every button press. The heartbeat dispatcher silences
  // notifications whose fireTime precedes this, so a press on any of the
  // household's buttons acknowledges (and stops) the notification on all of
  // them. Account-scoped on purpose — see web/functions/main.py.
  lastAckAt: timestamp.nullable().optional(),
  // Voice + phrasing config. Collapsed onto the account (was a per-account
  // `voiceProfiles` subcollection): all of a household's buttons behave
  // identically, so config is elder-level — same rationale as wake/bedtime.
  // The calendar set is every doc under accounts/{aid}/calendars/* (no stored
  // id-list); v1 has exactly one.
  voice: z.string().min(1).max(64),
  // Google TTS hard limits; UI clamps to a tighter band.
  speakingRate: z.number().min(0.25).max(4.0),
  pitch: z.number().min(-20).max(20),
  phrases: phrasesSchema.optional(),
  // Client-writable toggle for the morning notification nudge (see
  // phrases.morningPrompt). Defaults off; when on, the heartbeat dispatcher
  // flashes + (optionally) speaks the morning prompt at wake time.
  morningNudgeEnabled: z.boolean().optional(),
  // Client-writable toggle switching spoken clock times from the default
  // British am/pm wording to 24-hour ("nineteen thirty"). Defaults off.
  // Mirrors spoken_summary.py's Account.use_24_hour.
  use24HourTime: z.boolean().optional(),
  // Server-populated. Download URL for the canonical notification-prompt
  // audio (used when the notificationPrompt template has no event variables).
  // Stable URL with persistent token; regen_notification_audio_on_account_write
  // overwrites the underlying Storage file when voice / name / phrase text
  // changes. Optional: a brand-new account may not have had the trigger fire
  // yet, or the template embeds event variables (audio lives on each event).
  notificationPromptUrl: z.string().url().max(2048).optional(),
  // Server-populated. Download URL for the morning-nudge audio, synthesised
  // from phrases.morningPrompt ({name}-only); cleared when the nudge is
  // disabled or the prompt is empty.
  morningPromptUrl: z.string().url().max(2048).optional(),
  // Explicit UK GDPR Art 9 consent for health-related calendar/reminder
  // content, granted by the caregiver on the cared-for person's behalf via
  // the HealthConsentCheckbox when the profile is first created. Absent on
  // accounts scaffolded before naming (consent lands with the name) and on
  // pre-consent-feature accounts. Version identifies the wording shown.
  healthConsentAt: timestamp.optional(),
  healthConsentBy: z.string().optional(),
  healthConsentVersion: z.string().max(16).optional(),
  createdBy: z.string(),
  createdAt: timestamp.optional(),
  updatedAt: timestamp.optional(),
});
export type Account = z.infer<typeof accountSchema>;

/** Bump when the HealthConsentCheckbox wording changes materially — the
 * stored healthConsentVersion records which wording was agreed to. */
export const HEALTH_CONSENT_VERSION = 'v2';

// Hardware orders (accounts/{aid}/orders). Server-written: 'paid' by the
// Stripe webhook, the rest by fulfilment. Read-only on the client.
export const ORDER_STATUSES = [
  'paid',
  'preparing',
  'shipped',
  'delivered',
  'cancelled',
  'refunded',
] as const;
export const orderSchema = z.object({
  status: z.enum(ORDER_STATUSES),
  quantity: z.number().int(),
  amountTotal: z.number().nullable().optional(), // in pence
  currency: z.string().optional(),
  stripeSessionId: z.string().optional(),
  stripePaymentIntentId: z.string().nullable().optional(),
  // Delivery address collected at checkout (server-written from the
  // checkout.session.completed webhook). Where the button gets posted.
  shippingAddress: z
    .object({
      name: z.string().optional(),
      line1: z.string().optional(),
      line2: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      postalCode: z.string().optional(),
      country: z.string().optional(),
      phone: z.string().optional(),
    })
    .optional(),
  createdBy: z.string().optional(),
  createdAt: timestamp.optional(),
  shippedAt: timestamp.optional(),
  deliveredAt: timestamp.optional(),
  carrier: z.string().optional(),
  trackingNumber: z.string().optional(),
  trackingUrl: z.string().optional(),
});
export type Order = z.infer<typeof orderSchema>;

// Stripe billing state (accounts/{aid}/billing/state). Server-only;
// read on the client for the Settings billing panel. trialEnd /
// currentPeriodEnd are unix seconds (Stripe's representation).
export const billingStateSchema = z.object({
  stripeCustomerId: z.string().optional(),
  stripeSubscriptionId: z.string().nullable().optional(),
  stripePaymentMethodId: z.string().nullable().optional(),
  billingState: z.enum(['none', 'awaiting_connect', 'subscribed']).optional(),
  trialEnd: z.number().nullable().optional(),
  currentPeriodEnd: z.number().nullable().optional(),
  cancelAtPeriodEnd: z.boolean().optional(),
  // Server-written (Stripe webhook, billing.py): unix seconds of the last
  // processed subscription event (idempotency guard) + the write stamp.
  lastSubscriptionEventAt: z.number().optional(),
  updatedAt: timestamp.optional(),
});
export type BillingState = z.infer<typeof billingStateSchema>;

export const accountMembershipSchema = z.object({
  role: z.enum(['admin', 'caregiver']),
  accountName: z.string().max(100),
  addedAt: timestamp.optional(),
  // Server-written by the fan_out_account_name trigger when elderName changes.
  updatedAt: timestamp.optional(),
});
export type AccountMembership = z.infer<typeof accountMembershipSchema>;

export const memberSchema = z.object({
  role: z.enum(['admin', 'caregiver']),
  // Denormalised from users/{uid} at member-create time (auth trigger
  // for the founder, accept_invite for caregivers). Optional because
  // older member docs predate the denorm.
  email: z.string().email().optional(),
  displayName: z.string().max(100).optional(),
  createdAt: timestamp.optional(),
});
export type Member = z.infer<typeof memberSchema>;

export const calendarSchema = z.object({
  name: z.string().min(1).max(100),
  isPrimary: z.boolean().optional(),
  color: z.string().max(16).optional(),
  createdAt: timestamp.optional(),
  updatedAt: timestamp.optional(),
});
export type Calendar = z.infer<typeof calendarSchema>;

// Three-valued kind discriminator:
//   'event'    — in the daily summary, announced at its start time
//                ("at five o'clock"; "around now" while live).
//   'interval' — in the daily summary, announced as a time range
//                ("between five and six o'clock"; "between now and six"
//                while live). For delivery slots / windowed appointments.
//                Behaves as an event everywhere except this phrasing.
//   'reminder' — only spoken when the current moment is inside the
//                event's [start, end] window; title spoken verbatim,
//                no time framing. Default is 'event'.
export const eventKindSchema = z.enum(['event', 'interval', 'reminder']);
export const recurrenceSchema = z.enum(['none', 'daily', 'weekly']).optional();

// A notification is just an offset before the event. The audible/
// visual behaviour is governed account-wide by the
// `notificationPrompt` phrase template in the account config:
//   * empty template → silent (LED only)
//   * template without event vars → one account-level audio file
//   * template with {title} / {time} → per-event audio files
// See web/functions/spoken_summary.py:fill_notification_template.
export const eventNotificationSchema = z.object({
  // 0..1440 (0 min..24 hours). 0 means "fire at the event start".
  // The schema accepts up to 24h to keep render text simple (no
  // multi-day "tomorrow/next Wednesday" phrasing needed); the UI may
  // further restrict to 0 for kind='reminder' events (the press
  // summary only mentions such events inside their window, so any
  // non-zero offset would produce an inconsistent press follow-up).
  minutesBefore: z.number().int().min(0).max(1440),
});
export type EventNotification = z.infer<typeof eventNotificationSchema>;

export const eventSchema = z.object({
  title: z.string().min(1).max(200),
  startsAt: timestamp,
  endsAt: timestamp.optional(),
  allDay: z.boolean(),
  // Two-valued discriminator (see eventKindSchema). Default 'event'.
  kind: eventKindSchema.optional(),
  // 0 (or absent) = only mention on the day itself; N = mention from
  // N days before (with countdown text), then normally on the day.
  includeFromDaysBefore: z.number().int().min(0).max(365).optional(),
  // Pre-event fires (Google-Calendar-style "notifications"). For
  // kind='event' events, up to 5 offsets are allowed; for
  // kind='reminder' events, the UI restricts to one notification
  // at offset 0 (the event window is the only consistent fire
  // moment when the event isn't mentioned in the summary).
  notifications: z.array(eventNotificationSchema).max(5).optional(),
  // Server-populated. When the voice profile's notificationPrompt
  // template includes event variables ({title} / {time}), the
  // regen_notification_audio_on_event_write trigger synthesises a
  // per-event audio file and stores its download URL here.
  notificationAudioUrl: z.string().url().max(2048).optional(),
  recurrence: recurrenceSchema,
  recurrenceUntil: timestamp.optional(),
  // YYYY-MM-DD strings (in account tz) for occurrences to skip on a
  // recurring source. Empty / absent means no exclusions.
  excludedDates: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).max(365).optional(),
  createdBy: z.string(),
  createdAt: timestamp.optional(),
  updatedAt: timestamp.optional(),
});
export type EventDoc = z.infer<typeof eventSchema>;

export const deviceMirrorSchema = z.object({
  // A claimed device just binds to the account (config is account-level);
  // there's no per-device voice profile any more.
  roomLabel: z.string().max(100).optional(),
  claimedAt: timestamp.optional(),
  claimedBy: z.string().optional(),
  updatedAt: timestamp.optional(),
});
export type DeviceMirror = z.infer<typeof deviceMirrorSchema>;

export const buttonPressSchema = z.object({
  pressedAt: timestamp,
  deviceId: z.string().max(64),
  // Denormalised from accounts/{aid}/devices/{deviceId} at log time —
  // empty/absent for older presses or unlabelled devices.
  roomLabel: z.string().max(100).optional(),
});
export type ButtonPress = z.infer<typeof buttonPressSchema>;

export const alertSchema = z.object({
  hourOfDay: z.number().int().min(0).max(23),
  lookbackHours: z.number().int().min(1).max(24),
  recipientUids: z.array(z.string().min(1).max(64)).min(1).max(20),
  enabled: z.boolean(),
  createdAt: timestamp.optional(),
  updatedAt: timestamp.optional(),
});
export type Alert = z.infer<typeof alertSchema>;

export const alertDispatchSchema = z.object({
  alertId: z.string().max(64),
  firedAt: timestamp,
  hourOfDay: z.number().int().min(0).max(23),
  lookbackHours: z.number().int().min(1).max(24),
  // Absent/null when no prior press is on record. Server omits the
  // field entirely; we model it as nullable to be defensive on read.
  lastPressAt: timestamp.nullable().optional(),
  recipientsSent: z.array(z.string()).max(50),
  recipientsFailed: z
    .array(z.object({ email: z.string(), error: z.string() }))
    .max(50),
  outcome: z.enum(['sent', 'partial_send', 'no_recipients']),
  // Set on retry runs (partial_send → another attempt).
  lastAttemptAt: timestamp.nullable().optional(),
});
export type AlertDispatch = z.infer<typeof alertDispatchSchema>;

// ---- form schemas (HTML-form-friendly inputs) ---------------------

export const accountProfileFormSchema = z.object({
  elderName: z.string().min(1, 'Required').max(100),
  timeZone: z.string().min(1).max(64),
});
export type AccountProfileFormData = z.infer<typeof accountProfileFormSchema>;

export const calendarFormSchema = z.object({
  name: z.string().min(1, 'Required').max(100),
  color: z.string().max(16).optional(),
});
export type CalendarFormData = z.infer<typeof calendarFormSchema>;

export const eventFormSchema = z
  .object({
    kind: eventKindSchema.default('event'),
    title: z.string().min(1, 'Required').max(200),
    date: z.string().min(1, 'Required'),
    startTime: z.string().optional(),
    endTime: z.string().optional(),
    allDay: z.boolean().default(false),
    includeFromDaysBefore: z.coerce.number().int().min(0).max(365).optional(),
    notifications: z.array(eventNotificationSchema).max(5).optional(),
    recurrence: z.enum(['none', 'daily', 'weekly']).default('none'),
  })
  .refine(
    (v) => v.allDay || (v.startTime && v.startTime.length > 0),
    { message: 'Start time required unless all-day', path: ['startTime'] },
  );
export type EventFormData = z.infer<typeof eventFormSchema>;

export const inviteFormSchema = z.object({
  email: z.string().email(),
  role: z.enum(['admin', 'caregiver']).default('caregiver'),
});
export type InviteFormData = z.infer<typeof inviteFormSchema>;
